import { useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { useNavigate } from 'react-router-dom'
import { UserCircleIcon, LockClosedIcon, BellIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'

export default function SettingsPage() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'notifications' | 'system'>('profile')

  const handleLogout = () => {
    if (confirm('确定要退出登录吗？')) {
      logout()
      navigate('/auth/login')
    }
  }

  const tabs = [
    { id: 'profile', label: '个人资料', icon: UserCircleIcon },
    { id: 'security', label: '安全设置', icon: LockClosedIcon },
    { id: 'notifications', label: '通知设置', icon: BellIcon },
    { id: 'system', label: '系统配置', icon: ShieldCheckIcon },
  ]

  return (
    <div className="page-content">
      <div className="mb-8">
        <h1 className="page-title">设置</h1>
        <p className="page-subtitle">管理您的账户和系统配置</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'profile' && <ProfileTab user={user} />}
        {activeTab === 'security' && <SecurityTab onLogout={handleLogout} />}
        {activeTab === 'notifications' && <NotificationsTab />}
        {activeTab === 'system' && <SystemTab />}
      </div>
    </div>
  )
}

// Profile Tab
function ProfileTab({ user }: { user: any }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">个人信息</h2>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600">姓名</label>
            <p className="text-gray-900 mt-1">{user?.full_name || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">邮箱</label>
            <p className="text-gray-900 mt-1">{user?.email || '-'}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">角色</label>
            <p className="text-gray-900 mt-1">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                {user?.role === 'admin' ? '系统管理员' : user?.role === 'finance' ? '财务人员' : '审计人员'}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">权限</label>
            <div className="mt-2 space-y-2">
              {user?.permissions && user.permissions.length > 0 ? (
                user.permissions.map((perm: string, idx: number) => (
                  <span key={idx} className="inline-block mr-2 mb-2 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                    {perm}
                  </span>
                ))
              ) : (
                <p className="text-gray-500 text-sm">无特殊权限</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">账户状态</h2>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-600">账户状态</label>
            <p className="text-gray-900 mt-1">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                ✓ 正常
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">账户ID</label>
            <p className="text-gray-900 mt-1 font-mono text-sm">{user?.id?.substring(0, 8) || '-'}...</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">创建时间</label>
            <p className="text-gray-900 mt-1">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString('zh-CN') : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Security Tab
function SecurityTab({ onLogout }: { onLogout: () => void }) {
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' })

  const handleChangePassword = () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      alert('请填写所有字段')
      return
    }
    if (passwords.new !== passwords.confirm) {
      alert('新密码不一致')
      return
    }
    alert('密码修改功能开发中...')
    setShowPasswordForm(false)
    setPasswords({ current: '', new: '', confirm: '' })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">修改密码</h2>
        </div>
        <div className="card-body">
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="btn btn-primary"
            >
              修改密码
            </button>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">当前密码</label>
                <input
                  type="password"
                  value={passwords.current}
                  onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                  className="form-input"
                  placeholder="输入当前密码"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">新密码</label>
                <input
                  type="password"
                  value={passwords.new}
                  onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                  className="form-input"
                  placeholder="输入新密码"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">确认新密码</label>
                <input
                  type="password"
                  value={passwords.confirm}
                  onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                  className="form-input"
                  placeholder="确认新密码"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleChangePassword}
                  className="btn btn-primary flex-1"
                >
                  确认修改
                </button>
                <button
                  onClick={() => {
                    setShowPasswordForm(false)
                    setPasswords({ current: '', new: '', confirm: '' })
                  }}
                  className="btn btn-secondary flex-1"
                >
                  取消
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">登录安全</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-900">
              ✓ 您的账户已启用安全保护。建议定期修改密码以保护账户安全。
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">两步验证</label>
            <p className="text-gray-900 mt-1">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                未启用
              </span>
            </p>
          </div>
          <button className="btn btn-secondary w-full">启用两步验证</button>
        </div>
      </div>

      <div className="card lg:col-span-2">
        <div className="card-header">
          <h2 className="card-title">登出</h2>
        </div>
        <div className="card-body">
          <p className="text-gray-600 mb-4">点击下方按钮退出当前账户</p>
          <button
            onClick={onLogout}
            className="btn btn-danger"
          >
            退出登录
          </button>
        </div>
      </div>
    </div>
  )
}

// Notifications Tab
function NotificationsTab() {
  const [notifications, setNotifications] = useState({
    email_login: true,
    email_security: true,
    email_updates: false,
    sms_alerts: false,
  })

  const handleToggle = (key: string) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key as keyof typeof notifications],
    })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">邮件通知</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">登录通知</p>
              <p className="text-sm text-gray-600">账户登录时发送邮件</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.email_login}
                onChange={() => handleToggle('email_login')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">安全通知</p>
              <p className="text-sm text-gray-600">密码修改等安全事件</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.email_security}
                onChange={() => handleToggle('email_security')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">系统更新</p>
              <p className="text-sm text-gray-600">系统更新和维护通知</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.email_updates}
                onChange={() => handleToggle('email_updates')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">其他通知</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">短信告警</p>
              <p className="text-sm text-gray-600">重要告警通过短信发送</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={notifications.sms_alerts}
                onChange={() => handleToggle('sms_alerts')}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              通知设置已保存。您可以随时修改这些设置。
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// System Tab
function SystemTab() {
  const [settings, setSettings] = useState({
    maintenance_mode: false,
    api_rate_limit: 1000,
    session_timeout: 30,
    log_level: 'info',
  })

  const handleChange = (key: string, value: any) => {
    setSettings({
      ...settings,
      [key]: value,
    })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">系统状态</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
            <div>
              <p className="font-medium text-green-900">系统状态</p>
              <p className="text-sm text-green-700">正常运行</p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              ✓ 在线
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div>
              <p className="font-medium text-blue-900">API 状态</p>
              <p className="text-sm text-blue-700">所有服务正常</p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              ✓ 正常
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div>
              <p className="font-medium text-blue-900">数据库</p>
              <p className="text-sm text-blue-700">连接正常</p>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              ✓ 正常
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">系统配置</h2>
        </div>
        <div className="card-body space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">维护模式</label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.maintenance_mode}
                onChange={(e) => handleChange('maintenance_mode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API 速率限制 (请求/小时)</label>
            <input
              type="number"
              value={settings.api_rate_limit}
              onChange={(e) => handleChange('api_rate_limit', parseInt(e.target.value))}
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">会话超时 (分钟)</label>
            <input
              type="number"
              value={settings.session_timeout}
              onChange={(e) => handleChange('session_timeout', parseInt(e.target.value))}
              className="form-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">日志级别</label>
            <select
              value={settings.log_level}
              onChange={(e) => handleChange('log_level', e.target.value)}
              className="form-input"
            >
              <option value="debug">Debug</option>
              <option value="info">Info</option>
              <option value="warn">Warn</option>
              <option value="error">Error</option>
            </select>
          </div>
          <button className="btn btn-primary w-full">保存配置</button>
        </div>
      </div>

      <div className="card lg:col-span-2">
        <div className="card-header">
          <h2 className="card-title">系统信息</h2>
        </div>
        <div className="card-body grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600">系统版本</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">v1.0.0</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">运行时间</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">45天</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">活跃用户</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">1,234</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">API 调用</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">2.5M</p>
          </div>
        </div>
      </div>
    </div>
  )
}
